/**
 * Saudi payroll helpers — GOSI, SANED, EOSB, allowances, WPS.
 * No personal income tax (IRG) on salaries in KSA.
 */
import { isSaudiBrand } from '../../../products/saudi/utils/isSaudiBrand';
import { getBrandDefaultCurrency, getBrandLocale } from '../../../shared/utils/brandCurrency';

/** GOSI employee contribution on basic (Saudi nationals). */
export const GOSI_SAUDI_EMPLOYEE_RATE = 9.75;
/** GOSI employer contribution on basic (Saudi nationals). */
export const GOSI_SAUDI_EMPLOYER_RATE = 12.75;
/** Maximum basic salary subject to GOSI (SAR/month). */
export const GOSI_MAX_BASIC_SAR = 45000;

/** GOSI occupational hazards — non-Saudi: 2% employer only. */
export const GOSI_EXPAT_EMPLOYER_RATE = 2;
export const GOSI_EXPAT_EMPLOYEE_RATE = 0;

/** SANED unemployment insurance — Saudi nationals: 2% (1% + 1%). */
export const SANED_EMPLOYER_RATE = 1;
export const SANED_EMPLOYEE_RATE = 1;

/** Default overtime multiplier (150%). */
export const DEFAULT_OVERTIME_RATE = 1.5;

/** WPS payment deadline (day of month). */
export const WPS_PAYMENT_DAY = 7;

/** Typical housing allowance as % of basic salary. */
export const DEFAULT_HOUSING_ALLOWANCE_PCT = 25;

/** Default monthly working days for payroll proration. */
export const DEFAULT_MONTHLY_WORKING_DAYS = 30;

/** Default monthly allowances (SAR) when not configured. */
export const DEFAULT_SAUDI_ALLOWANCES = {
  housingPct: DEFAULT_HOUSING_ALLOWANCE_PCT,
  transport: 1500,
  mobile: 500,
  food: 800,
};

/** Saudi banks for WPS (name → SWIFT/BIC prefix). */
export const SAUDI_BANKS = [
  { code: 'NCBK', name: 'Al Rajhi Bank / SNB (NCB)' },
  { code: 'RJHISARI', name: 'Al Rajhi Bank' },
  { code: 'SABBSARI', name: 'SABB (HSBC)' },
  { code: 'RIBLSARI', name: 'Riyad Bank' },
  { code: 'INMASARI', name: 'Alinma Bank' },
  { code: 'BSFRSARI', name: 'Banque Saudi Fransi' },
  { code: 'ARNBSARI', name: 'Arab National Bank' },
  { code: 'BJAZSAJE', name: 'Bank AlJazira' },
  { code: 'GULFSARI', name: 'Gulf International Bank' },
];

/** Validate Saudi IBAN: SA + 22 digits. */
export function isValidSaudiIban(iban) {
  const normalized = String(iban || '').replace(/\s/g, '').toUpperCase();
  return /^SA\d{22}$/.test(normalized);
}

/** BCP-47 locale for payroll dates. */
export function getPayrollLocale(language) {
  return getBrandLocale(language);
}

/** Detect Saudi nationality from common employee fields. */
export function isSaudiNational(employee = {}) {
  if (employee.isSaudi === true || employee.estSaudi === true) return true;
  if (employee.estEtranger === true || employee.isExpat === true) return false;

  const nat = String(
    employee.nationality || employee.nationalite || employee.country || ''
  ).toUpperCase();
  return nat === 'SA' || nat === 'SAU' || nat === 'SAUDI' || nat.includes('سعود');
}

/** Cap GOSI contributory base: basic + housing, max SAR 45,000 (Saudi GOSI rules). */
export function getGosiContributoryBase(basicSalary, housingAllowance = 0, maxBasic = GOSI_MAX_BASIC_SAR) {
  const basic = Math.max(parseFloat(basicSalary) || 0, 0);
  const housing = Math.max(parseFloat(housingAllowance) || 0, 0);
  return Math.min(basic + housing, maxBasic);
}

/** @deprecated use getGosiContributoryBase — basic only, no housing */
export function getGosiContributoryBasic(basicSalary, maxBasic = GOSI_MAX_BASIC_SAR) {
  return getGosiContributoryBase(basicSalary, 0, maxBasic);
}

/** Housing allowance (typically 25% of basic). */
export function calculateHousingAllowance(basicSalary, pct = DEFAULT_HOUSING_ALLOWANCE_PCT) {
  const basic = parseFloat(basicSalary) || 0;
  return Math.round(basic * (pct / 100) * 100) / 100;
}

/** Standard Saudi allowance bundle from basic salary. */
export function calculateSaudiAllowances(basicSalary, overrides = {}) {
  const basic = parseFloat(basicSalary) || 0;
  const housingPct = overrides.housingPct ?? DEFAULT_SAUDI_ALLOWANCES.housingPct;
  return {
    housing: calculateHousingAllowance(basic, housingPct),
    transport: overrides.transport ?? DEFAULT_SAUDI_ALLOWANCES.transport,
    mobile: overrides.mobile ?? DEFAULT_SAUDI_ALLOWANCES.mobile,
    food: overrides.food ?? DEFAULT_SAUDI_ALLOWANCES.food,
    total:
      calculateHousingAllowance(basic, housingPct) +
      (overrides.transport ?? DEFAULT_SAUDI_ALLOWANCES.transport) +
      (overrides.mobile ?? DEFAULT_SAUDI_ALLOWANCES.mobile) +
      (overrides.food ?? DEFAULT_SAUDI_ALLOWANCES.food),
  };
}

/**
 * GOSI contributions on basic salary (capped at GOSI_MAX_BASIC_SAR).
 * @returns {{ employee, employer, total, employeeRate, employerRate, contributoryBasic }}
 */
export function calculateGOSIContributions(basicSalary, employee = {}, settings = {}) {
  const maxBasic = settings.gosiMaxBasic ?? GOSI_MAX_BASIC_SAR;
  const basic = parseFloat(basicSalary) || 0;
  const housing =
    settings.housingAllowance ??
    calculateHousingAllowance(basic, settings.housingPct ?? DEFAULT_HOUSING_ALLOWANCE_PCT);
  const contributoryBasic = getGosiContributoryBase(basic, housing, maxBasic);
  const saudi = isSaudiNational(employee);

  const employeeRate = saudi
    ? (settings.gosiEmployeeRate ?? GOSI_SAUDI_EMPLOYEE_RATE)
    : GOSI_EXPAT_EMPLOYEE_RATE;
  const employerRate = saudi
    ? (settings.gosiEmployerRate ?? GOSI_SAUDI_EMPLOYER_RATE)
    : GOSI_EXPAT_EMPLOYER_RATE;

  const employeeAmt = Math.round(contributoryBasic * employeeRate) / 100;
  const employerAmt = Math.round(contributoryBasic * employerRate) / 100;

  return {
    employee: employeeAmt,
    employer: employerAmt,
    total: employeeAmt + employerAmt,
    employeeRate,
    employerRate,
    contributoryBasic,
    isSaudi: saudi,
  };
}

/**
 * SANED unemployment — Saudi nationals only.
 */
export function calculateSANEDContributions(basicSalary, employee = {}, settings = {}) {
  const maxBasic = settings.gosiMaxBasic ?? GOSI_MAX_BASIC_SAR;
  const basic = parseFloat(basicSalary) || 0;
  const housing =
    settings.housingAllowance ??
    calculateHousingAllowance(basic, settings.housingPct ?? DEFAULT_HOUSING_ALLOWANCE_PCT);
  const contributoryBasic = getGosiContributoryBase(basic, housing, maxBasic);
  if (!isSaudiNational(employee)) {
    return { employee: 0, employer: 0, total: 0, employeeRate: 0, employerRate: 0 };
  }

  const employeeRate = settings.sanedEmployeeRate ?? SANED_EMPLOYEE_RATE;
  const employerRate = settings.sanedEmployerRate ?? SANED_EMPLOYER_RATE;
  const employeeAmt = Math.round(contributoryBasic * employeeRate) / 100;
  const employerAmt = Math.round(contributoryBasic * employerRate) / 100;

  return {
    employee: employeeAmt,
    employer: employerAmt,
    total: employeeAmt + employerAmt,
    employeeRate,
    employerRate,
  };
}

/** EOSB monthly accrual estimate (½ month per year ≈ 4.17% of basic). */
export function calculateEOSBProvision(basicSalary) {
  const basic = parseFloat(basicSalary) || 0;
  return Math.round(basic * (1 / 24) * 100) / 100;
}

/**
 * Gross = Basic + Housing + Transport + Other allowances + overtime + bonuses.
 */
export function calculateGrossSalary(basicSalary, allowances = {}, extras = {}) {
  const basic = parseFloat(basicSalary) || 0;
  const housing = allowances.housing ?? calculateHousingAllowance(basic);
  const transport = allowances.transport ?? DEFAULT_SAUDI_ALLOWANCES.transport;
  const mobile = allowances.mobile ?? DEFAULT_SAUDI_ALLOWANCES.mobile;
  const food = allowances.food ?? DEFAULT_SAUDI_ALLOWANCES.food;
  const overtime = parseFloat(extras.overtime) || 0;
  const bonuses = parseFloat(extras.bonuses) || 0;
  return Math.round((basic + housing + transport + mobile + food + overtime + bonuses) * 100) / 100;
}

/**
 * Full Saudi payroll deduction summary.
 * Net = Gross - GOSI (if Saudi) - SANED (if Saudi) - Other deductions.
 * No income tax withheld on salaries.
 */
export function calculateSaudiPayrollSummary(employee, grossSalary, settings = {}) {
  const basic = parseFloat(employee?.salaireBase ?? employee?.salary ?? grossSalary) || 0;
  const allowanceOverrides = settings?.allowances || {};
  const allowances = calculateSaudiAllowances(basic, allowanceOverrides);
  const gross = parseFloat(grossSalary) || calculateGrossSalary(basic, allowances, settings?.extras || {});

  const gosiSettings = { ...settings, housingAllowance: allowances.housing };
  const gosi = calculateGOSIContributions(basic, employee, gosiSettings);
  const saned = calculateSANEDContributions(basic, employee, gosiSettings);
  const eosb = calculateEOSBProvision(basic);

  const otherDeductions = parseFloat(settings?.otherDeductions) || 0;
  const totalEmployeeDeductions = gosi.employee + saned.employee + otherDeductions;
  const totalEmployerContributions = gosi.employer + saned.employer;
  const netPay = gross - totalEmployeeDeductions;

  return {
    salaireBrut: gross,
    salaireBase: basic,
    allowances,
    contributoryBase: gosi.contributoryBasic,
    retenueGOSISalarie: gosi.employee,
    retenueGOSIEmployeur: gosi.employer,
    retenueSANEDSalarie: saned.employee,
    retenueSANEDEmployeur: saned.employer,
    retenueSSSalarie: totalEmployeeDeductions,
    retenueSSEmployeur: totalEmployerContributions,
    provisionEOSB: eosb,
    cacobatCongesPayes: eosb,
    congesPayes: eosb,
    salaireImposable: gross,
    retenueIRG: 0,
    brmRetenueIRG: 0,
    netComptable: netPay,
    netAPayer: netPay,
    gosi,
    saned,
    currency: getBrandDefaultCurrency(),
  };
}

/**
 * Generate WPS SIF (Salary Information File) lines.
 * @param {Array} employees - { id, nameAr, name, iban, netSalary, bankCode, workingDays }
 */
export function generateWpsSifLines(employees, periodLabel) {
  return employees.map((emp, idx) => {
    const iban = String(emp.iban || emp.bank_account || '').replace(/\s/g, '').toUpperCase();
    if (!isValidSaudiIban(iban)) {
      throw new Error(`Invalid Saudi IBAN for employee ${emp.id || idx + 1}`);
    }
    return [
      String(emp.id || emp.employee_id || '').padStart(10, '0'),
      emp.nameAr || emp.name || '',
      iban,
      (parseFloat(emp.netSalary ?? emp.net_to_pay ?? emp.montant) || 0).toFixed(2),
      emp.bankCode || iban.substring(4, 8),
      String(emp.workingDays ?? 30).padStart(2, '0'),
      periodLabel || '',
    ].join('|');
  });
}

/** @deprecated alias */
export const capGosiBasic = getGosiContributoryBasic;
/** @deprecated alias */
export const GOSI_PENSION_EMPLOYEE_RATE = GOSI_SAUDI_EMPLOYEE_RATE;
/** @deprecated alias */
export const GOSI_PENSION_EMPLOYER_RATE = GOSI_SAUDI_EMPLOYER_RATE;
export const GOSI_EMPLOYEE_TOTAL_RATE = GOSI_SAUDI_EMPLOYEE_RATE + SANED_EMPLOYEE_RATE;
export const GOSI_EMPLOYER_TOTAL_RATE = GOSI_SAUDI_EMPLOYER_RATE + SANED_EMPLOYER_RATE;

/** Whether payroll should use Saudi rules (always true for Qawaem brand). */
export function useSaudiPayrollRules() {
  return isSaudiBrand();
}

/** Saudi Labor Law — max penalty deduction (days' wages). */
export const SAUDI_MAX_PENALTY_DAYS = 5;

/** Advance/loan limits (months of salary). Islamic finance — no interest (riba). */
export const SAUDI_ADVANCE_MAX_MONTHS = 6;
export const SAUDI_ADVANCE_MIN_MONTHS = 3;

/** Overtime — hourly rate = basic ÷ 240; regular OT at 150%. */
export const SAUDI_MONTHLY_HOURS_DIVISOR = 240;
export const SAUDI_OVERTIME_RATE = DEFAULT_OVERTIME_RATE;

/** Default Saudi bonus types (fallback when API empty). */
export const DEFAULT_SAUDI_BONUSES = [
  { id: 1, code: 'BON', designationKey: 'bonus_annual', type: 'pourcentage', valeurDefaut: 100, plafond: 200, cotisable: true, imposable: false, descriptionKey: 'bonus_annual_desc' },
  { id: 2, code: 'PERF', designationKey: 'bonus_performance', type: 'pourcentage', valeurDefaut: 10, plafond: 30, cotisable: false, imposable: false, descriptionKey: 'bonus_performance_desc' },
  { id: 3, code: 'EID', designationKey: 'bonus_eid', type: 'fixe', valeurDefaut: 5000, plafond: null, cotisable: false, imposable: false, descriptionKey: 'bonus_eid_desc' },
  { id: 4, code: 'RAM', designationKey: 'bonus_ramadan', type: 'fixe', valeurDefaut: 3000, plafond: null, cotisable: false, imposable: false, descriptionKey: 'bonus_ramadan_desc' },
  { id: 5, code: 'PROJ', designationKey: 'bonus_project', type: 'fixe', valeurDefaut: 10000, plafond: null, cotisable: false, imposable: false, descriptionKey: 'bonus_project_desc' },
  { id: 6, code: 'COMM', designationKey: 'bonus_commission', type: 'pourcentage', valeurDefaut: 5, plafond: 20, cotisable: false, imposable: false, descriptionKey: 'bonus_commission_desc' },
];

/** Default Saudi allowance types (بدلات). */
export const DEFAULT_SAUDI_ALLOWANCE_TYPES = [
  { id: 1, code: 'HOU', designationKey: 'allowance_housing', type: 'pourcentage', valeurDefaut: 25, plafond: 40, cotisable: true, imposable: false, descriptionKey: 'allowance_housing_desc' },
  { id: 2, code: 'TRN', designationKey: 'allowance_transport', type: 'fixe', valeurDefaut: 750, plafond: 1000, cotisable: false, imposable: false, descriptionKey: 'allowance_transport_desc' },
  { id: 3, code: 'MOB', designationKey: 'allowance_mobile', type: 'fixe', valeurDefaut: 300, plafond: 500, cotisable: false, imposable: false, descriptionKey: 'allowance_mobile_desc' },
  { id: 4, code: 'FOD', designationKey: 'allowance_food', type: 'fixe', valeurDefaut: 800, plafond: null, cotisable: false, imposable: false, descriptionKey: 'allowance_food_desc' },
  { id: 5, code: 'EDU', designationKey: 'allowance_education', type: 'fixe', valeurDefaut: 2000, plafond: null, cotisable: false, imposable: false, descriptionKey: 'allowance_education_desc' },
  { id: 6, code: 'HSH', designationKey: 'allowance_hardship', type: 'pourcentage', valeurDefaut: 15, plafond: 25, cotisable: false, imposable: false, descriptionKey: 'allowance_hardship_desc' },
];

/** Deduction type values (English API keys). */
export const SAUDI_DEDUCTION_TYPE_VALUES = {
  gosi: 'gosi',
  loanRepayment: 'loan_repayment',
  absence: 'absence',
  lateness: 'lateness',
  penalty: 'penalty',
  other: 'other',
};

/** Per diem rates by Saudi city (SAR/day). */
export const SAUDI_PER_DIEM_RATES = {
  riyadh: 800,
  jeddah: 750,
  dammam: 700,
  makkah: 750,
  madinah: 700,
  khobar: 700,
  abha: 650,
  international: 1500,
  default: 700,
};

export function getPerDiemRate(cityKey) {
  if (!cityKey) return SAUDI_PER_DIEM_RATES.default;
  const key = String(cityKey).toLowerCase();
  return SAUDI_PER_DIEM_RATES[key] ?? SAUDI_PER_DIEM_RATES.default;
}

/** Cap penalty to max 5 days' wages (Saudi labor law). */
export function capPenaltyAmount(dailyWage, penaltyDays = 1) {
  const wage = Number(dailyWage) || 0;
  const days = Math.min(Math.max(Number(penaltyDays) || 1, 0), SAUDI_MAX_PENALTY_DAYS);
  return Math.round(days * wage * 100) / 100;
}

/** Daily wage from monthly basic (8 h/day). */
export function dailyWageFromBasic(monthlyBasic) {
  const hourly = (Number(monthlyBasic) || 0) / SAUDI_MONTHLY_HOURS_DIVISOR;
  return hourly * 8;
}

/** Validate advance/loan against 3–6 months salary rule. */
export function validateAdvanceAmount(amount, monthlySalary) {
  const amt = Number(amount) || 0;
  const salary = Number(monthlySalary) || 0;
  if (salary <= 0) return { valid: amt > 0, maxAmount: null };
  const maxAmount = salary * SAUDI_ADVANCE_MAX_MONTHS;
  return { valid: amt > 0 && amt <= maxAmount, maxAmount };
}

/** Map seed row to UI shape with i18n labels. */
export function mapSaudiPayrollTypeRow(item, t, ns) {
  const p = (key, opts) => t(`${ns}.${key}`, opts);
  return {
    ...item,
    designation: p(item.designationKey),
    description: item.descriptionKey ? p(item.descriptionKey) : '',
  };
}

/** Hourly rate from monthly basic (÷ 240). */
export function calculateHourlyRateFromBasic(monthlyBasic) {
  const monthly = Number(monthlyBasic) || 0;
  if (monthly <= 0) return 0;
  return monthly / SAUDI_MONTHLY_HOURS_DIVISOR;
}

/** Overtime pay at 150% (or custom rate). */
export function calculateSaudiOvertimePay(hours, hourlyRate, rate = SAUDI_OVERTIME_RATE) {
  return (Number(hours) || 0) * (Number(hourlyRate) || 0) * rate;
}

// ─────────────────────────────────────────────────────────────────────────────
// GOSI Declaration & WPS Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** GOSI declaration rates (total including SANED) — static defaults; prefer getGosiDeclRates(settings). */
export const GOSI_DECL_EMPLOYEE_RATE = GOSI_EMPLOYEE_TOTAL_RATE;
export const GOSI_DECL_EMPLOYER_RATE = GOSI_EMPLOYER_TOTAL_RATE;

/** Map salaries module config → GOSI/SANED calculation settings. */
export function mapSalariesConfigToGosiSettings(config = {}) {
  return {
    gosiEmployeeRate: config.gosiEmployeeRate,
    gosiEmployerRate: config.gosiEmployerRate,
    gosiExpatEmployerRate: config.gosiExpatEmployerRate,
    gosiMaxBasic: config.gosiMaxBasic,
    sanedEmployeeRate: config.sanedEmployeeRate,
    sanedEmployerRate: config.sanedEmployerRate,
    housingPct: config.housingAllowancePct,
    transport: config.transportAllowance,
    mobile: config.mobileAllowance,
    food: config.foodAllowance,
  };
}

/** Live GOSI declaration display rates from module config. */
export function getGosiDeclRates(settings = {}) {
  const gosiEmp = settings.gosiEmployeeRate ?? GOSI_SAUDI_EMPLOYEE_RATE;
  const gosiEr = settings.gosiEmployerRate ?? GOSI_SAUDI_EMPLOYER_RATE;
  const sanedEmp = settings.sanedEmployeeRate ?? SANED_EMPLOYEE_RATE;
  const sanedEr = settings.sanedEmployerRate ?? SANED_EMPLOYER_RATE;
  return {
    employeeRate: gosiEmp + sanedEmp,
    employerRate: gosiEr + sanedEr,
    gosiEmployeeRate: gosiEmp,
    gosiEmployerRate: gosiEr,
    sanedEmployeeRate: sanedEmp,
    sanedEmployerRate: sanedEr,
    gosiMaxBasic: settings.gosiMaxBasic ?? GOSI_MAX_BASIC_SAR,
  };
}

/**
 * Get GOSI payment deadline for a given month (10th of the month).
 * @param {number} year - Full year (e.g., 2026)
 * @param {number} month - Month (1-12)
 * @returns {Date}
 */
export function getGOSIPaymentDeadline(year, month) {
  return new Date(year, month - 1, 10);
}

/**
 * Get GOSI submission deadline (15th of the month).
 * @param {number} year - Full year
 * @param {number} month - Month (1-12)
 * @returns {Date}
 */
export function getGOSISubmissionDeadline(year, month) {
  return new Date(year, month - 1, 15);
}

/**
 * Get WPS payment deadline (7th of the month).
 * @param {number} year - Full year
 * @param {number} month - Month (1-12)
 * @returns {Date}
 */
export function getWpsDeadline(year, month) {
  return new Date(year, month - 1, WPS_PAYMENT_DAY);
}

/**
 * Calculate GOSI declaration line for a single employee.
 * @param {Object} employee - { id, name, basicSalary, nationality, isSaudi }
 * @returns {Object} - { employeeId, name, contributoryBasic, employeeGOSI, employerGOSI, totalGOSI }
 */
export function calculateGOSIDeclarationLine(employee, settings = {}) {
  const basic = parseFloat(employee?.salaireBase ?? employee?.basicSalary ?? employee?.salary ?? 0) || 0;
  const gosi = calculateGOSIContributions(basic, employee, settings);
  const saned = calculateSANEDContributions(basic, employee, settings);
  const housingPct = settings.housingPct ?? DEFAULT_HOUSING_ALLOWANCE_PCT;
  const housing = settings.housingAllowance ?? calculateHousingAllowance(basic, housingPct);
  const employeeContrib = gosi.employee + saned.employee;
  const employerContrib = gosi.employer + saned.employer;
  const declRates = getGosiDeclRates(settings);

  return {
    employeeId: employee.id || employee.employee_id || '',
    name: employee.name || `${employee.prenom || employee.firstName || ''} ${employee.nom || employee.lastName || ''}`.trim(),
    nationalId: employee.nationalId || employee.cin || '',
    contributoryBasic: gosi.contributoryBasic,
    basic,
    allowances: housing,
    employeeGOSI: gosi.employee,
    employerGOSI: gosi.employer,
    employeeContrib,
    employerContrib,
    pensionEmployee: gosi.employee,
    sanedEmployee: saned.employee,
    totalGOSI: gosi.total + saned.total,
    isSaudi: gosi.isSaudi,
    employeeRate: declRates.employeeRate,
    employerRate: declRates.employerRate,
    isContributor: gosi.isSaudi || employerContrib > 0,
    highRisk: false,
    employee,
  };
}

/**
 * Calculate GOSI declaration summary for multiple employees.
 * @param {Array} employees - Array of employee objects
 * @returns {Object} - { totalContributoryBasic, totalEmployeeGOSI, totalEmployerGOSI, totalGOSI, employeeCount, saudiCount, expatCount }
 */
export function calculateGOSIDeclarationSummary(employees, settings = {}) {
  const empty = {
    totalContributoryBasic: 0,
    totalEmployeeGOSI: 0,
    totalEmployerGOSI: 0,
    totalGOSI: 0,
    employeeCount: 0,
    saudiCount: 0,
    expatCount: 0,
    totalRegistered: 0,
    totalBasicSalaries: 0,
    totalEmployeeContrib: 0,
    totalEmployerContrib: 0,
    totalContributions: 0,
    lines: [],
  };

  if (!Array.isArray(employees) || employees.length === 0) {
    return empty;
  }

  const lines = employees.map((emp) => calculateGOSIDeclarationLine(emp, settings));
  let totalContributoryBasic = 0;
  let totalEmployeeGOSI = 0;
  let totalEmployerGOSI = 0;
  let totalGOSI = 0;
  let totalBasicSalaries = 0;
  let totalEmployeeContrib = 0;
  let totalEmployerContrib = 0;
  let saudiCount = 0;
  let expatCount = 0;

  lines.forEach((line) => {
    totalContributoryBasic += line.contributoryBasic;
    totalBasicSalaries += line.basic;
    totalEmployeeGOSI += line.employeeGOSI;
    totalEmployerGOSI += line.employerGOSI;
    totalEmployeeContrib += line.employeeContrib;
    totalEmployerContrib += line.employerContrib;
    totalGOSI += line.totalGOSI;
    if (line.isSaudi) saudiCount += 1;
    else expatCount += 1;
  });

  const round = (n) => Math.round(n * 100) / 100;

  return {
    totalContributoryBasic: round(totalContributoryBasic),
    totalEmployeeGOSI: round(totalEmployeeGOSI),
    totalEmployerGOSI: round(totalEmployerGOSI),
    totalGOSI: round(totalGOSI),
    employeeCount: employees.length,
    saudiCount,
    expatCount,
    totalRegistered: employees.length,
    totalBasicSalaries: round(totalBasicSalaries),
    totalEmployeeContrib: round(totalEmployeeContrib),
    totalEmployerContrib: round(totalEmployerContrib),
    totalContributions: round(totalEmployeeContrib + totalEmployerContrib),
    lines,
  };
}

/**
 * Format a date for payroll display using ar-SA locale.
 * @param {Date|string|number} date - Date to format
 * @param {string} format - 'short', 'medium', 'long' (default: 'medium')
 * @returns {string}
 */
export function formatPayrollDate(date, format = 'medium') {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';

  const locale = getPayrollLocale('ar');
  const options = {
    short: { year: '2-digit', month: 'numeric', day: 'numeric' },
    medium: { year: 'numeric', month: 'short', day: 'numeric' },
    long: { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' },
  };

  return d.toLocaleDateString(locale, options[format] || options.medium);
}

/**
 * Count Saudi vs expat split in employee list.
 * @param {Array} employees - Employee array
 * @returns {Object} - { saudi, expat, total }
 */
export function countNationalitySplit(employees) {
  if (!Array.isArray(employees)) {
    return { saudi: 0, expat: 0, total: 0 };
  }

  let saudi = 0;
  let expat = 0;

  employees.forEach((emp) => {
    if (isSaudiNational(emp)) {
      saudi++;
    } else {
      expat++;
    }
  });

  return { saudi, expat, total: employees.length };
}

/**
 * Resolve an employee's gross salary from basic + allowances or from direct gross field.
 * @param {Object} employee - { salaireBase, salaireBrut, basicSalary, grossSalary, allowances, ... }
 * @param {Object} settings - Optional allowance overrides
 * @returns {number}
 */
export function resolveEmployeeGrossSalary(employee, settings = {}) {
  if (!employee) return 0;

  // Try direct gross salary fields
  const directGross = employee.salaireBrut || employee.grossSalary || employee.gross_salary || 0;
  if (directGross > 0) return parseFloat(directGross);

  // Calculate from basic
  const basic = employee.salaireBase || employee.basicSalary || employee.basic_salary || employee.salary || 0;
  if (basic <= 0) return 0;

  const allowanceOverrides = settings?.allowances || {};
  const allowances = calculateSaudiAllowances(basic, allowanceOverrides);
  return calculateGrossSalary(basic, allowances, settings?.extras || {});
}

/** Alias for resolveEmployeeGrossSalary */
export const calculateEmployeeGrossSalary = resolveEmployeeGrossSalary;



